import type { Member, MemberSection } from "./types";

const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

export function lastName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.at(-1) ?? name;
}

export function memberInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = Array.from(parts[0])[0] ?? "";
  const last = parts.length > 1 ? (Array.from(parts.at(-1) ?? "")[0] ?? "") : "";
  return (first + last).toLocaleUpperCase();
}

export function attendanceSections(members: Member[], query: string, attending: boolean): MemberSection[] {
  const needle = query.trim().toLocaleLowerCase();
  const matches = members
    .filter((member) => member.attending === attending)
    .filter((member) => !needle || member.name.toLocaleLowerCase().includes(needle))
    .sort((left, right) => {
      const byLast = collator.compare(lastName(left.name), lastName(right.name));
      return byLast || collator.compare(left.name, right.name);
    });

  const sections: MemberSection[] = [];
  for (const member of matches) {
    const firstCharacter = Array.from(lastName(member.name))[0]?.toLocaleUpperCase() ?? "#";
    const letter = /\p{L}/u.test(firstCharacter) ? firstCharacter : "#";
    let section = sections.at(-1);
    if (!section || section.letter !== letter) {
      section = { letter, members: [] };
      sections.push(section);
    }
    section.members.push(member);
  }
  return sections;
}

export function remainingSections(members: Member[], query: string): MemberSection[] {
  return attendanceSections(members, query, false);
}

export function formattedEventDate(eventDate: string | undefined, header: string): string {
  let value = eventDate;
  if (!value) {
    const compact = header.match(/^(\d{2})(\d{2})(\d{2})/);
    if (compact) value = `20${compact[1]}-${compact[2]}-${compact[3]}`;
  }
  if (!value) {
    const short = header.match(/^(\d{1,2})\/(\d{1,2})$/);
    if (short) value = `${new Date().getFullYear()}-${short[1].padStart(2, "0")}-${short[2].padStart(2, "0")}`;
  }
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return header;
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.valueOf())) return header;
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(date);
}
